import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, X } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { assistentApi, type AssistentVorschlag, type AssistentVorschlagZeile } from '@/lib/assistent/assistent.api'
import { fmtDate } from '@/lib/format-date'
import { verweisPfad } from './Antworttext'
import { meldeAenderung } from '@/lib/assistent/assistent-beilagen'

/**
 * AP-18 7.4 — ein **Vorschlag** des Assistenten: Zeilen in Alltagssprache mit Häkchen, darunter
 * „Übernehmen" und „Verwerfen". Geändert wird erst mit dem Klick — mit den Rechten des Benutzers
 * und den Prüfungen von dann. Was scheitert, trägt seinen Grund an der Zeile und bleibt wählbar.
 *
 * Die Zeilen kommen vom Server ohne ihre Daten: Übernommen wird über die Kennung der Zeile.
 */
export function Vorschlagsliste({ vorschlag: v, onGeaendert }: { vorschlag: AssistentVorschlag; onGeaendert: (v: AssistentVorschlag) => void }) {
  const { t } = useTranslation()
  const waehlbar = (z: AssistentVorschlagZeile) => z.status === 'OPEN' || z.status === 'FAILED'
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(() => new Set(v.items.filter(waehlbar).map((z) => z.id)))
  const [laeuft, setLaeuft] = useState<'apply' | 'reject' | null>(null)
  // Der Ersatztext entsteht erst beim Anzeigen (kein Übersetzer im Zustand).
  const [fehler, setFehler] = useState<string | null>(null)

  // Nach einer Übernahme: Was nicht mehr wählbar ist, ist auch nicht mehr gewählt.
  const offeneIds = v.items.filter(waehlbar).map((z) => z.id).join(',')
  useEffect(() => {
    const offen = new Set(offeneIds ? offeneIds.split(',') : [])
    setGewaehlt((alt) => new Set([...alt].filter((id) => offen.has(id))))
  }, [offeneIds])

  const anzahl = v.items.filter((z) => waehlbar(z) && gewaehlt.has(z.id)).length

  async function tue(was: 'apply' | 'reject') {
    setLaeuft(was)
    setFehler(null)
    try {
      const neu = was === 'apply' ? await assistentApi.vorschlagUebernehmen(v.id, [...gewaehlt]) : await assistentApi.vorschlagVerwerfen(v.id)
      onGeaendert(neu)
      // Eine Seite, die zeigt, was dieses Werkzeug anlegt, lädt neu (`useAenderung('werkzeug', '<name>', …)`).
      if (was === 'apply' && neu.items.some((z) => z.status === 'APPLIED')) meldeAenderung('werkzeug', neu.tool)
    } catch (e) {
      setFehler(apiErrorText(e, ''))
    } finally {
      setLaeuft(null)
    }
  }

  const schluss = v.status === 'APPLIED' ? 'applied' : v.status === 'REJECTED' ? 'rejected' : v.status === 'EXPIRED' ? 'expired' : v.status === 'PARTIAL' && !v.open ? 'partial' : null

  return (
    <section className="rounded-lg border border-border bg-card/60 p-3" aria-label={v.title}>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h4 className="text-sm font-medium">{v.title}</h4>
        {v.open && <span className="text-[11px] text-muted-foreground">{t('assistant.proposal.notYet')}</span>}
      </div>
      <ul className="space-y-1.5">
        {v.items.map((z) => {
          const pfad = z.link ? verweisPfad(z.link) : null
          return (
            <li key={z.id} className="text-sm">
              <label className={`flex items-start gap-2 ${z.status === 'REJECTED' ? 'text-muted-foreground line-through' : ''}`}>
                {v.open && waehlbar(z) ? (
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    checked={gewaehlt.has(z.id)}
                    disabled={laeuft !== null}
                    onChange={(e) =>
                      setGewaehlt((alt) => {
                        const neu = new Set(alt)
                        if (e.target.checked) neu.add(z.id)
                        else neu.delete(z.id)
                        return neu
                      })
                    }
                  />
                ) : z.status === 'APPLIED' ? (
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-label={t('assistant.proposal.lineApplied')} />
                ) : (
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                )}
                <span className="min-w-0 flex-1 break-words">{z.text}</span>
              </label>
              {(z.status === 'APPLIED' && (pfad || z.note)) || z.error ? (
                <div className="ml-6 mt-0.5 text-xs">
                  {z.status === 'APPLIED' && z.note && <span className="text-muted-foreground">{z.note} </span>}
                  {z.status === 'APPLIED' && pfad && (
                    <Link to={pfad} className="text-primary hover:underline">
                      {t('assistant.proposal.openLink')}
                    </Link>
                  )}
                  {z.error && z.status !== 'APPLIED' && <span className="text-destructive">{apiErrorText({ response: { data: z.error } }, t('assistant.proposal.lineFailed'))}</span>}
                </div>
              ) : null}
            </li>
          )
        })}
      </ul>

      {fehler !== null && <p className="mt-2 text-xs text-destructive">{fehler || t('assistant.proposal.error')}</p>}

      {v.open ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="inline-flex h-8 items-center rounded-md bg-primary px-3 text-sm text-primary-foreground disabled:opacity-50"
            disabled={laeuft !== null || anzahl === 0}
            onClick={() => void tue('apply')}
          >
            {laeuft === 'apply' ? t('assistant.proposal.applying') : v.items.length > 1 ? t('assistant.proposal.applyCount', { count: anzahl }) : t('assistant.proposal.apply')}
          </button>
          <button type="button" className="inline-flex h-8 items-center rounded-md border border-border px-3 text-sm hover:bg-accent disabled:opacity-50" disabled={laeuft !== null} onClick={() => void tue('reject')}>
            {t('assistant.proposal.reject')}
          </button>
          <span className="ml-auto text-[11px] text-muted-foreground">{t('assistant.proposal.validUntil', { date: fmtDate(v.expiresAt) })}</span>
        </div>
      ) : schluss ? (
        <p className="mt-2 text-xs text-muted-foreground" role="status">
          {t(`assistant.proposal.${schluss}`)}
        </p>
      ) : v.status === 'APPLYING' ? (
        <p className="mt-2 text-xs text-muted-foreground" role="status">
          {t('assistant.proposal.applying')}
        </p>
      ) : null}
    </section>
  )
}
